/**
 * ThreatFormModal — wraps the shared FormModal with the Step 4
 * threat field schema. Drives both Add and Edit flows.
 *
 * Three external option sources are injected at render time:
 *   - `assetOptions`    — from Step 2 asset list (asset_id select)
 *   - `scenarioOptions` — from Step 3 damage scenarios (damage_ids multi-select)
 *
 * `compromised_property` is auto-derived from `stride_category` on
 * save (consumer responsibility — `derivePropertyFromStride` in
 * `threatFieldSchema`). The form does NOT expose this field.
 */
import React, { useMemo } from 'react';
import FormModal from '../../../../components/ui/FormModal/FormModal';
import { THREAT_FORM_FIELDS, type ThreatRecord } from './threatFieldSchema';

const FormModalAny = FormModal as unknown as React.ComponentType<any>;

export interface OptionLite {
  value: string;
  label: string;
}

export interface ThreatFormModalProps {
  mode: 'add' | 'edit';
  initialThreat?: ThreatRecord | null;
  assetOptions: OptionLite[];
  scenarioOptions: OptionLite[];
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

export default function ThreatFormModal({
  mode,
  initialThreat,
  assetOptions,
  scenarioOptions,
  onSubmit,
  onClose,
}: ThreatFormModalProps) {
  const isEdit = mode === 'edit';

  const fields = useMemo(
    () =>
      THREAT_FORM_FIELDS.map((f) => {
        if (f.name === 'asset_id') return { ...f, options: assetOptions };
        if (f.name === 'damage_ids') return { ...f, options: scenarioOptions };
        if (isEdit && f.name === 'threat_id') return { ...f, disabled: true };
        return f;
      }),
    [assetOptions, scenarioOptions, isEdit],
  );

  return (
    <FormModalAny
      title={isEdit ? 'Edit Threat' : 'Add Threat'}
      fields={fields}
      initialValues={initialThreat ?? {}}
      submitLabel={isEdit ? 'Save Changes' : 'Add Threat'}
      submittingLabel={isEdit ? 'Saving…' : 'Adding…'}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth={720}
    />
  );
}
